import { consentAttemptStorageKey, pendingOAuthSnapshot, validatedPendingConsent } from './pendingOAuth.js';

export function personalPublishingConsent(pending, userId, baseURL, now = Date.now()) {
  if (!['chatgpt', 'claude'].includes(pending?.connector)
    || pending?.canContinueWithPersonalWorkspace !== true) return null;
  return validatedPendingConsent(pending, { userId, baseURL, now });
}

// The destination remains the server's existing consent page; preparing a
// workspace never grants scopes or approves OAuth.
export async function continuePersonalPublishing({ pending, userId, apiClient, navigate,
  isCurrent = () => true, baseURL, now = () => Date.now(), requireSingleChatGPT = false }) {
  const target = personalPublishingConsent(pending, userId, baseURL, now());
  if (!target || !isCurrent()) return { status: 'blocked' };
  const read = async () => pendingOAuthSnapshot(
    await apiClient.auth.getPendingOAuth({ connector: pending.connector }), userId, baseURL);
  const latest = await read();
  if (!isCurrent()) return { status: 'stale' };
  if (personalPublishingConsent(latest, userId, baseURL, now()) !== target
    || latest?.connector !== pending.connector) return { status: 'changed', pending: latest };
  // The two hosts have independent pending cookies. Automatic continuation
  // must not pick ChatGPT while another host is pending (or cannot be read).
  const onlyChatGPT = async () => !requireSingleChatGPT
    || (await apiClient.auth.getPendingOAuth({ connector: 'claude' }))?.pending === false;
  if (!await onlyChatGPT()) return { status: 'manual', pending: latest };
  if (!isCurrent()) return { status: 'stale' };
  if (personalPublishingConsent(latest, userId, baseURL, now()) !== target) return { status: 'changed', pending: latest };
  const response = await apiClient.onboarding.createPersonalWorkspace();
  if (!isCurrent()) return { status: 'stale' };
  const data = response?.data || response;
  if (typeof data?.brandProfileId !== 'string' || !data.brandProfileId) throw new Error('Workspace setup did not return a saved workspace');
  const finalPending = await read();
  if (!isCurrent()) return { status: 'stale' };
  if (personalPublishingConsent(finalPending, userId, baseURL, now()) !== target
    || finalPending?.connector !== pending.connector) return { status: 'changed', pending: finalPending };
  if (!await onlyChatGPT()) return { status: 'manual', pending: finalPending };
  if (!isCurrent()) return { status: 'stale' };
  if (personalPublishingConsent(finalPending, userId, baseURL, now()) !== target) {
    return { status: 'changed', pending: finalPending };
  }
  navigate(target);
  return { status: 'navigated', pending: finalPending };
}

// Use the same opaque attempt identity as paid consent navigation. The lock
// covers workspace preparation too; the marker survives reloads and tabs so
// only one automatic navigation can consume the shared consent CSRF cookie.
export async function attemptAutomaticPersonalPublishing({ pending, userId, storage, locks,
  subtle, visible = true, isCurrent = () => true, baseURL, navigate, ...options }) {
  if (pending?.connector !== 'chatgpt' || pending.automatic === false || !visible
    || !personalPublishingConsent(pending, userId, baseURL, options.now?.()) || !isCurrent()) return { status: 'blocked' };
  if (!storage || typeof locks?.request !== 'function') return { status: 'manual', pending };
  let key;
  try { key = await consentAttemptStorageKey(pending, userId, subtle, baseURL); } catch { return { status: 'manual', pending }; }
  if (!key) return { status: 'manual', pending };
  if (!isCurrent()) return { status: 'stale' };
  try {
    return await locks.request(`${key}.lock`, { mode: 'exclusive' }, async () => {
      if (!isCurrent()) return { status: 'stale' };
      if (storage.getItem(key) === '1') return { status: 'attempted', pending };
      return continuePersonalPublishing({ ...options, pending, userId, baseURL, isCurrent,
        requireSingleChatGPT: true,
        navigate: url => {
          storage.setItem(key, '1');
          if (storage.getItem(key) !== '1') throw new Error('Cannot record connection attempt');
          // No await between the final lifecycle check, marker and hand-off.
          if (!isCurrent()) throw new Error('Connection changed before navigation');
          navigate(url);
        },
      });
    });
  } catch {
    return { status: 'failed', pending };
  }
}
